import {useEffect, useState} from 'react';
import {DataTotal} from "../../../../api/types";
import useSWR from "swr";
import urls from "../../../../api/urls";
import {api} from "../../../../api/apiProvider";
import useSWRMutation from "swr/mutation";

export interface DataTotalsFilterInterface {
  channels: number[];
  factors: number[];
}

export default function useDataTotals(periodId: number, filter: DataTotalsFilterInterface) {
  const [dataTotals, setDataTotals] = useState<DataTotal[]>([]);

  const getDataTotalsParams = () => {
    const params = new URLSearchParams([
      ['t[]', 'period'], ['t[]', 'period:data'], ['t[]', 'data'], ['t[]', 'data:metrics'], ['t[]', 'data:channels'],
      ['t[]', 'dataChannel'], ['t[]', 'dataChannel:channel'], ['t[]', 'data:file'], ['t[]', 'period:plan'],
      ['t[]', 'media_object:read'], ['t[]', 'dataFactor:factor'], ['t[]', 'factor'], ['t[]', 'channel'],
      ['t[]', 'data:nonMediaDataFactor'], ['channel.isActive', 'true'],
    ]);

    if (filter && filter.channels) {
      for (let channelId of filter.channels) {
        params.append('filterComponents[channels][]', String(channelId));
      }
    }

    if (filter && filter.factors) {
      for (let factorId of filter.factors) {
        params.append('filterComponents[factors][]', String(factorId));
      }
    }

    return params.toString();
  };

  const {
    trigger: mutateDataTotal,
    isMutating: isDataTotalLoading,
  } = useSWRMutation<{
    data: DataTotal
  }, any, string, number>(
    urls.dataTotals,
    async (url, {arg: dataTotalId}: { arg: number }) => {
      const fullUrl = `${urls.dataTotals}/${dataTotalId}?${getDataTotalsParams()}`;
      return api.get(fullUrl);
    },
    {
      onSuccess: (newDataTotal) => {
        if (newDataTotal?.data) {
          mutateDataTotals((currentData) => {
            if (!currentData?.data) {
              return { data: [newDataTotal.data] };
            }

            const existingIndex = currentData.data.findIndex(
              item => item.id === newDataTotal.data.id
            );

            let updatedData;

            if (existingIndex !== -1) {
              updatedData = currentData.data.map(item =>
                item.id === newDataTotal.data.id ? newDataTotal.data : item
              );
            } else {
              updatedData = [newDataTotal.data, ...currentData.data];
            }

            return {
              ...currentData,
              data: updatedData
            };
          }, false);
        }
      },
    }
  );

  const {
    data: dataTotalsData,
    isValidating: isDataTotalsLoading,
    mutate: mutateDataTotals,
  } = useSWR<{
    data: DataTotal[],
  }>(periodId > 0 ? `${urls.dataTotals}?period.id=${periodId}&${getDataTotalsParams()}` : null, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (dataTotalsData && dataTotalsData.data) {
      const dataTotals = dataTotalsData.data;

      dataTotals.sort((a, b) => {
        if (a.isFact && !b.isFact) {
          return -1;
        }

        if (!a.isFact && b.isFact) {
          return 1;
        }
        return 0;
      });

      setDataTotals(dataTotals);
    }
  }, [dataTotalsData]);

  const isFactData = (): boolean => {
    return Boolean(dataTotalsData && dataTotalsData.data && dataTotalsData.data.length > 0 && dataTotalsData.data.some((dataTotal: DataTotal) => dataTotal.isFact))
  };

  return ({
    dataTotals,
    isDataTotalsLoading,
    mutateDataTotal,
    mutateDataTotals,
    isFactData: isFactData(),
    isDataTotalLoading,
  });
}
